"use client";

import {
  useGetArtworkById,
  useGetRelatedFieldsWithAttachments,
} from "@/api/artwork/artwork";
import {
  shouldIncludeAttachment,
  useGetAttachmentsByDocId,
} from "@/api/attachment";
import { MultilingualSelect } from "@/components/form-items/MultilingualField";
import { ImageFullscreen } from "@/components/modal/imageFullscreen";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { SupportedLocale } from "@/i18n";
import {
  computeFieldDimensionsArtwork,
  computeFieldLocalisationHistoriqueArtwork,
  computeFieldRelatedAuctions,
  computeFieldRelatedAuthorsArtwork,
  computeFieldRelatedBibliographies,
  computeFieldRelatedExhibitions,
  computeFieldSignatureArtwork,
  computeFieldSupportTechniqueArtwork,
} from "@/lib/computeFields";
import { getDefaultAttachment } from "@/lib/image";
import { PublicationStatus } from "@/lib/types";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

const ArtworkOverview = () => {
  const { i18n, t } = useTranslation();
  const [localLocale, setLocalLocale] = useState<SupportedLocale>(
    i18n.language as SupportedLocale,
  );
  const [translationsLoaded, setTranslationsLoaded] = useState(0);
  const [isLoadingTranslations, setIsLoadingTranslations] = useState(false);
  const navigate = useNavigate();

  // Recharger les traductions depuis l'API quand la langue locale change
  useEffect(() => {
    // Ne pas afficher le loader au premier chargement
    const isInitialLoad = translationsLoaded === 0;

    if (!isInitialLoad) {
      setIsLoadingTranslations(true);
    }

    // Recharger les ressources i18n pour la nouvelle langue depuis l'API
    i18n
      .reloadResources(localLocale, "translation")
      .then(() => {
        // Forcer le re-render en mettant à jour l'état après le chargement
        setTranslationsLoaded((prev) => prev + 1);
        setIsLoadingTranslations(false);
      })
      .catch((error) => {
        console.warn("Failed to reload translations:", error);
        setIsLoadingTranslations(false);
      });
    // translationsLoaded est utilisé uniquement pour détecter le premier chargement, ne pas l'inclure dans les dépendances
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localLocale, i18n]);

  // Fonction pour obtenir une traduction dans une langue spécifique sans modifier la langue de l'interface
  const getTranslation = useMemo(() => {
    const loadResourceBundle = () => {
      // Essayer d'abord depuis i18n (qui devrait avoir les ressources après reloadResources)
      let resourceBundle = i18n.getResourceBundle(localLocale, "translation");
      if (resourceBundle) return resourceBundle;

      // Si pas trouvé dans i18n, charger depuis localStorage
      try {
        // i18next-localstorage-backend utilise le format: i18next_res_{lang}-{namespace}
        const possibleKeys = [
          `i18next_res_${localLocale}-translation`, // Format avec tiret (le bon format)
        ];

        for (const storageKey of possibleKeys) {
          const stored = localStorage.getItem(storageKey);
          if (stored) {
            try {
              const parsed = JSON.parse(stored);

              // Le format peut être { translation: {...} } ou directement {...}
              if (parsed) {
                resourceBundle = parsed.translation || parsed;
                if (resourceBundle && typeof resourceBundle === "object") {
                  // Ajouter les ressources à i18n pour les prochaines fois
                  i18n.addResourceBundle(
                    localLocale,
                    "translation",
                    resourceBundle,
                    true,
                    true,
                  );
                  return resourceBundle;
                }
              }
            } catch (_parseError) {
              // Continuer avec la clé suivante
              continue;
            }
          }
        }
      } catch (e) {
        console.warn("Could not load translations from localStorage:", e);
      }

      return resourceBundle;
    };

    return (key: string, options?: any): string => {
      const resourceBundle = loadResourceBundle();

      // Si on a trouvé la ressource, utiliser la traduction
      if (resourceBundle && resourceBundle[key]) {
        let translation = resourceBundle[key];

        // Gérer l'interpolation si des options sont fournies
        if (options && typeof translation === "string") {
          Object.keys(options).forEach((optKey) => {
            if (optKey !== "lng" && optKey !== "count") {
              translation = translation.replace(
                new RegExp(`{{${optKey}}}`, "g"),
                String(options[optKey]),
              );
            }
          });
        }

        return typeof translation === "string" ? translation : key;
      }

      // Fallback: utiliser la fonction de traduction fixée
      try {
        const fixedT = i18n.getFixedT(localLocale, "translation");
        const result = fixedT(key, options);
        return typeof result === "string" ? result : key;
      } catch {
        // Dernier fallback: retourner la clé
        return key;
      }
    };
    // translationsLoaded est nécessaire pour forcer le recalcul quand les traductions sont rechargées
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n, localLocale, translationsLoaded]);

  const { id } = useParams();

  const { data: artwork } = useGetArtworkById(id);
  const { data: relatedFields } = useGetRelatedFieldsWithAttachments(id);
  const { data: unfiltetredAttachments } = useGetAttachmentsByDocId(id);

  const attachments =
    artwork &&
    unfiltetredAttachments?.filter((a) =>
      shouldIncludeAttachment(
        a,
        artwork.oeuvresPublicationStatus as PublicationStatus,
      ),
    );

  const mainImage = getDefaultAttachment(attachments ?? [], "artwork");
  const otherImages =
    attachments
      ?.filter((attachment: any) => attachment.id !== mainImage?.id)
      ?.sort((a: any, b: any) => a.order - b.order) ?? [];

  const supportTechniqueCalculated = useMemo(
    () =>
      artwork
        ? computeFieldSupportTechniqueArtwork(
            {
              ...artwork,
              preposition: getTranslation(
                "computeField.techniqueSupportPreposition",
              ),
              doubleFace: getTranslation("computeField.doubleFace"),
            },
            {
              language: localLocale,
            },
          )
        : undefined,
    [artwork, localLocale, getTranslation],
  );

  const dimensionsCalculated = useMemo(
    () =>
      artwork
        ? computeFieldDimensionsArtwork(artwork, {
            language: localLocale,
          })
        : "",
    [artwork, localLocale],
  );

  const signatureCalculated = useMemo(
    () =>
      artwork
        ? computeFieldSignatureArtwork(artwork, {
            language: localLocale,
          })
        : undefined,
    [artwork, localLocale],
  );

  const historyCalculated = useMemo(
    () =>
      artwork
        ? computeFieldLocalisationHistoriqueArtwork(artwork, {
            language: localLocale,
          })
        : undefined,
    [artwork, localLocale],
  );

  const exhibitionsCalculated = useMemo(
    () =>
      artwork
        ? computeFieldRelatedExhibitions(
            {
              relatedExhbitions: artwork.artworkInExhibitions,
            },
            { language: localLocale },
          )
        : undefined,
    [artwork, localLocale],
  );

  const auctionCalculated = useMemo(
    () =>
      artwork
        ? computeFieldRelatedAuctions(
            {
              relatedAuctions: artwork.artworkInAuctions,
            },
            { language: localLocale },
          )
        : undefined,
    [artwork, localLocale],
  );

  const bibliographyCalculated = useMemo(
    () =>
      artwork
        ? computeFieldRelatedBibliographies(
            {
              relatedBibliographies: artwork.artworkInBibliography,
            },
            { language: localLocale },
          )
        : undefined,
    [artwork, localLocale],
  );

  const artistsCalculated = useMemo(
    () =>
      artwork
        ? computeFieldRelatedAuthorsArtwork(artwork, {
            language: localLocale,
          })
        : undefined,
    [artwork, localLocale],
  );

  const relationNotEmpty = useMemo(() => {
    if (relatedFields) {
      return (
        relatedFields.relatedArtwork.length > 0 ||
        relatedFields.from_Artwork_relatedArtwork.length > 0 ||
        relatedFields.relatedAudiovisual.length > 0 ||
        relatedFields.relatedDocument.length > 0 ||
        relatedFields.relatedMultipleprint.length > 0 ||
        relatedFields.relatedPhotography.length > 0 ||
        relatedFields.relatedWriting.length > 0
      );
    }
    return false;
  }, [relatedFields]);

  if (!artwork) {
    return <div>{t("artworkNexistePasLabel")}</div>;
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between w-full h-[70px] bg-[#FCFCFD] border-y px-5">
        <p></p>
        <p className="font-semibold text-lg xs:hidden md:block">
          {artwork.oeuvresDescriptionTitre}
        </p>
        <div className="flex items-center gap-3">
          <MultilingualSelect
            value={localLocale}
            onValueChange={(value) => setLocalLocale(value as SupportedLocale)}
          />
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-sm font-semibold text-gray-950 cursor-pointer border"
          >
            {t("Close")}
          </Button>
        </div>
      </div>
      <div className="w-full relative">
        {isLoadingTranslations && (
          <div className="fixed inset-0 top-[70px] bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
          </div>
        )}
        <div className="w-full max-w-screen-2xl m-auto md:p-10 xs:p-5">
          <div className="flex xs:flex-col md:flex-row items-start justify-center text-center gap-10">
            <div className="w-full flex xs:flex-col md:flex-row gap-4 text-left relative">
              {otherImages.length > 0 && (
                <div className="md:max-w-20 xs:w-full flex md:flex-col xs:flex-row gap-4 md:order-1 xs:order-2 z-10 md:sticky md:top-4">
                  {otherImages?.map((attachment: any) => (
                    <ImageFullscreen
                      media={{
                        attachments: [attachment],
                        type: "artwork",
                        alt:
                          localLocale === "fr"
                            ? attachment.textAltFR
                            : attachment.textAltEN,
                        width: 500,
                        height: 500,
                        className: "xs:max-w-20 md:w-full",
                      }}
                      key={attachment.id}
                    />
                  ))}
                </div>
              )}
              {mainImage && (
                <div
                  className={`xs:ml-0 w-full md:order-2 xs:order-1 ${otherImages.length > 0 ? "md:w-[calc(100%-6rem)]" : ""}`}
                >
                  <ImageFullscreen
                    media={{
                      attachments: [mainImage],
                      type: "artwork",
                      alt:
                        (localLocale === "fr"
                          ? mainImage.textAltFR
                          : mainImage.textAltEN) ?? "",
                      width: 900,
                      height: 900,
                      className: "w-full object-contain",
                    }}
                  />
                </div>
              )}
            </div>
            <div className="w-full h-full flex flex-col gap-5 text-left md:pr-16">
              <div className="flex items-center justify-between">
                <p className="font-normal text-base">{artistsCalculated}</p>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 mb-1 w-full">
                  <p className="font-semibold text-4xl w-full">
                    {artwork.oeuvresDescriptionTitre}
                  </p>
                  <span className="border-r border-1 border-gray-400 h-9"></span>
                  <HoverCard>
                    <HoverCardTrigger className="font-semibold text-lg w-full text-black cursor-pointer">
                      {artwork.oeuvresDescriptionNumeroEnregistrement}
                    </HoverCardTrigger>
                    <HoverCardContent
                      className="font-semibold text-xs p-2 border-none shadow"
                      side="top"
                    >
                      {t("singleArtworkHoverCardNumber")}
                    </HoverCardContent>
                  </HoverCard>
                </div>
              </div>
              <div className="mb-1">
                <p className="font-normal text-base">
                  {artwork.oeuvresDescriptionDateDeCreation}
                </p>
                {
                  <p className="font-normal text-sm">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: supportTechniqueCalculated ?? "",
                      }}
                    />
                  </p>
                }
                <div
                  className="font-semibold text-sm"
                  dangerouslySetInnerHTML={{
                    __html: dimensionsCalculated,
                  }}
                />
                {signatureCalculated && (
                  <p
                    className="font-normal text-sm"
                    dangerouslySetInnerHTML={{ __html: signatureCalculated }}
                  />
                )}
                <div className="mt-1">
                  <p className="font-normal text-sm">
                    {artwork?.oeuvresDescriptionSousTitre
                      ? getTranslation("singleArtworkSubtitleSousTitre") +
                        " " +
                        artwork?.oeuvresDescriptionSousTitre
                      : ""}
                  </p>
                  <p className="font-normal text-sm">
                    {artwork?.oeuvresDescriptionAutreTitre
                      ? getTranslation("singleArtworkSubtitleAutreTitre") +
                        " " +
                        artwork?.oeuvresDescriptionAutreTitre
                      : ""}
                  </p>
                  <p className="font-normal text-sm">
                    {artwork?.oeuvresDescriptionReference
                      ? getTranslation("singleArtworkSubtitleReference") +
                        " " +
                        artwork?.oeuvresDescriptionReference
                      : ""}
                  </p>
                </div>
              </div>
              <Accordion
                type="single"
                collapsible
                className="font-medium text-base text-gray-800 mb-32"
              >
                {artwork &&
                  artwork.artworkHistory &&
                  artwork.artworkHistory.length > 0 && (
                    <AccordionItem value="history">
                      <AccordionTrigger>
                        {getTranslation("singleArtworkSubtitleHistory")}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: historyCalculated ?? "",
                          }}
                        />
                      </AccordionContent>
                    </AccordionItem>
                  )}
                {artwork?.oeuvresValidationRemarquesPubliquesMultilingual[
                  localLocale
                ] && (
                  <AccordionItem value="pub-notes">
                    <AccordionTrigger>
                      {getTranslation("singleArtworkSubtitlePublicNote", {
                        count: 0,
                      })}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div
                        dangerouslySetInnerHTML={{
                          __html:
                            artwork
                              ?.oeuvresValidationRemarquesPubliquesMultilingual[
                              localLocale
                            ] ?? "",
                        }}
                      />
                    </AccordionContent>
                  </AccordionItem>
                )}
                {artwork.artworkInExhibitions &&
                  artwork.artworkInExhibitions.length > 0 && (
                    <AccordionItem value="expositions">
                      <AccordionTrigger>
                        {getTranslation("singleArtworkSubtitleExhibition")}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: exhibitionsCalculated ?? "",
                          }}
                        />
                      </AccordionContent>
                    </AccordionItem>
                  )}
                {artwork.artworkInBibliography &&
                  artwork.artworkInBibliography.length > 0 && (
                    <AccordionItem value="bibliographie">
                      <AccordionTrigger>
                        {getTranslation("singleArtworkSubtitleBibliography")}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: bibliographyCalculated ?? "",
                          }}
                        />
                      </AccordionContent>
                    </AccordionItem>
                  )}
                {artwork.artworkInAuctions &&
                  artwork.artworkInAuctions.length > 0 && (
                    <AccordionItem value="auctions">
                      <AccordionTrigger>
                        {getTranslation("singleArtworkSubtitleAuction")}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: auctionCalculated ?? "",
                          }}
                        />
                      </AccordionContent>
                    </AccordionItem>
                  )}
                {artwork?.oeuvresCreditsCreditOeuvre &&
                  artwork?.oeuvresCreditsCreditPhotoOeuvre && (
                    <AccordionItem value="credits">
                      <AccordionTrigger>
                        {getTranslation("singleArtworkSubtitleCredits")}
                      </AccordionTrigger>
                      <AccordionContent>
                        <p>
                          {artwork?.oeuvresCreditsCreditOeuvre} |{" "}
                          {artwork?.oeuvresCreditsCreditPhotoOeuvre}
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  )}
                {relationNotEmpty && (
                  <AccordionItem value="oeuvres">
                    <AccordionTrigger>
                      {getTranslation("singleArtworkSubtitleAssociatedWorks")}
                    </AccordionTrigger>
                    <AccordionContent className="flex flex-col gap-4 w-full">
                      {relatedFields.relatedArtwork.map((artwork: any) => (
                        <div key={artwork.id}>
                          <div className="flex items-center gap-4 w-full">
                            {artwork.attachments &&
                            artwork.attachments.length > 0 ? (
                              <ImageFullscreen
                                media={{
                                  attachments: artwork.attachments,
                                  type: "artwork",
                                  alt:
                                    artwork.oeuvresDescriptionTitre ||
                                    "Artwork image",
                                  width: 200,
                                  height: 200,
                                  className:
                                    "h-[100px] min-w-[200px] aspect-auto object-right-top",
                                }}
                              />
                            ) : (
                              <div className="w-[100px] h-[100px] bg-gray-200 flex items-center justify-center rounded">
                                <span className="text-gray-400">
                                  {t("noImage")}
                                </span>
                              </div>
                            )}
                            <div
                              dangerouslySetInnerHTML={{
                                __html: artwork.oeuvresDescriptionResume,
                              }}
                            />
                          </div>
                        </div>
                      ))}
                      {relatedFields.from_Artwork_relatedArtwork.map(
                        (artwork: any) => (
                          <div key={artwork.id}>
                            <div className="flex items-center gap-4 w-full">
                              {artwork.attachments &&
                              artwork.attachments.length > 0 ? (
                                <ImageFullscreen
                                  media={{
                                    attachments: artwork.attachments,
                                    type: "artwork",
                                    alt:
                                      artwork.oeuvresDescriptionTitre ||
                                      "Artwork image",
                                    width: 200,
                                    height: 200,
                                    className:
                                      "h-[100px] min-w-[200px] aspect-auto object-right-top",
                                  }}
                                />
                              ) : (
                                <div className="w-[100px] h-[100px] bg-gray-200 flex items-center justify-center rounded">
                                  <span className="text-gray-400">
                                    No image
                                  </span>
                                </div>
                              )}
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: artwork.oeuvresDescriptionResume,
                                }}
                              />
                            </div>
                          </div>
                        ),
                      )}
                      {relatedFields.relatedDocument.map((document: any) => (
                        <div key={document.id}>
                          <div className="flex items-center gap-4">
                            <div className="max-w-[200px]">
                              {document.attachments &&
                              document.attachments.length > 0 ? (
                                <ImageFullscreen
                                  media={{
                                    attachments: document.attachments,
                                    type: "document",
                                    alt:
                                      document.documentsDescriptionTitre ||
                                      "Document image",
                                    width: 200,
                                    height: 200,
                                    className:
                                      "h-[100px] min-w-[200px] aspect-auto !object-right-top",
                                  }}
                                />
                              ) : (
                                <div className="w-[100px] h-[100px] bg-gray-200 flex items-center justify-center rounded">
                                  <span className="text-gray-400">
                                    No image
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <p>{document.documentsDescriptionTitre}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                      {relatedFields.relatedMultipleprint.map(
                        (multipleprint: any) => (
                          <div key={multipleprint.id}>
                            <div className="flex items-center gap-4">
                              {multipleprint.attachments &&
                              multipleprint.attachments.length > 0 ? (
                                <ImageFullscreen
                                  media={{
                                    attachments: multipleprint.attachments,
                                    type: "multipleprint",
                                    alt:
                                      multipleprint.estampesMultiplesDescriptionTitre ||
                                      "Multipleprint image",
                                    width: 200,
                                    height: 200,
                                    className:
                                      "h-[100px] min-w-[200px] aspect-auto !object-right-top",
                                  }}
                                />
                              ) : (
                                <div className="w-[100px] h-[100px] bg-gray-200 flex items-center justify-center rounded">
                                  <span className="text-gray-400">
                                    No image
                                  </span>
                                </div>
                              )}
                              <div
                                dangerouslySetInnerHTML={{
                                  __html:
                                    multipleprint.estampesMultiplesDescriptionResume,
                                }}
                              />
                            </div>
                          </div>
                        ),
                      )}
                      {relatedFields.relatedPhotography.map(
                        (photography: any) => (
                          <div key={photography.id}>
                            <div className="flex gap-4 items-center">
                              <div>
                                {photography.attachments &&
                                photography.attachments.length > 0 ? (
                                  <ImageFullscreen
                                    media={{
                                      attachments: photography.attachments,
                                      type: "photography",
                                      alt:
                                        photography.photosDescriptionLegendeRedigee ||
                                        "Photography image",
                                      width: 200,
                                      height: 200,
                                      className:
                                        "w-[200px] h-[100px] aspect-auto !object-right-top",
                                    }}
                                  />
                                ) : (
                                  <div className="w-[100px] h-[100px] bg-gray-200 flex items-center justify-center rounded">
                                    <span className="text-gray-400">
                                      No image
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div
                                className="flex-1"
                                dangerouslySetInnerHTML={{
                                  __html:
                                    photography.photosDescriptionLegendeRedigee,
                                }}
                              />
                            </div>
                          </div>
                        ),
                      )}
                      {relatedFields.relatedWriting.map((writing: any) => (
                        <div key={writing.id}>
                          <div className="flex items-center gap-4">
                            <div className="w-[200px]">
                              {writing.attachments &&
                              writing.attachments.length > 0 ? (
                                <ImageFullscreen
                                  media={{
                                    attachments: writing.attachments,
                                    type: "writing",
                                    alt:
                                      writing.documentsDescriptionTitre ||
                                      "Writing image",
                                    width: 200,
                                    height: 200,
                                    className:
                                      "h-[100px] min-w-[200px] aspect-auto !object-right-top",
                                  }}
                                />
                              ) : (
                                <div className="w-[100px] h-[100px] bg-gray-200 flex items-center justify-center rounded">
                                  <span className="text-gray-400">
                                    No image
                                  </span>
                                </div>
                              )}
                            </div>
                            <div
                              className="flex-1"
                              dangerouslySetInnerHTML={{
                                __html: writing.ecritsDescriptionLegendeRedigee,
                              }}
                            />
                          </div>
                        </div>
                      ))}
                      {relatedFields.relatedAudiovisual.map(
                        (audiovisual: any) => (
                          <div key={audiovisual.id}>
                            <div className="flex items-center gap-4">
                              <div className="w-[200px]">
                                {audiovisual.attachments &&
                                audiovisual.attachments.length > 0 ? (
                                  <ImageFullscreen
                                    media={{
                                      attachments: audiovisual.attachments,
                                      type: "audiovisual",
                                      alt:
                                        audiovisual.audiovisuelDescriptionResume ||
                                        "Audiovisual image",
                                      width: 200,
                                      height: 200,
                                      className:
                                        "h-[100px] min-w-[200px] aspect-auto object-right-top",
                                    }}
                                  />
                                ) : (
                                  <div className="w-[100px] h-[100px] bg-gray-200 flex items-center justify-center rounded">
                                    <span className="text-gray-400">
                                      No image
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div className="flex-1">
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html:
                                      audiovisual.audiovisuelDescriptionResume,
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        ),
                      )}
                    </AccordionContent>
                  </AccordionItem>
                )}
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtworkOverview;
